import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PromotionKind, fromMinorUnits, type Promotion } from '@lombok/shared';
import { Button, Card, CardContent } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { BackLink } from '@/components/DetailRow';
import { FORM_COLUMN } from '@/components/layout';
import { QueryState } from '@/components/QueryState';
import { usePromotion, useUpdatePromotion } from '@/data/hooks';
import { formatDecimalInput } from '@/lib/decimal';
import { PromotionFormFields } from './PromotionFormFields';
import { clearFieldError, promotionServerError, validatePromotionForm } from './promotionForm';
import type { PromotionFormErrors, PromotionFormValues } from './promotionForm';

/**
 * Editar promoción (admin). Loads the promotion, pre-fills the form, and PATCHes only the
 * changed fields. Fixed amounts round-trip through USD (minor units stored / decimal shown);
 * dates round-trip through the browser-local datetime input.
 */
export function PromocionEditPage({ basePath }: { basePath: string }) {
  const { id } = useParams<{ id: string }>();
  const query = usePromotion(id);

  return (
    <div className={`flex flex-col gap-6 ${FORM_COLUMN}`}>
      <div>
        <BackLink to={basePath}>Promociones</BackLink>
        <PageHeader title="Editar promoción" description="Actualiza los detalles de la promoción." />
      </div>
      <Card>
        <QueryState
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          onRetry={() => void query.refetch()}
        >
          {query.data ? <EditForm promotion={query.data} basePath={basePath} /> : null}
        </QueryState>
      </Card>
    </div>
  );
}

function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EditForm({ promotion, basePath }: { promotion: Promotion; basePath: string }) {
  const navigate = useNavigate();
  const update = useUpdatePromotion();
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<PromotionFormErrors>({});
  const [form, setForm] = useState<PromotionFormValues>(() => ({
    name: promotion.name,
    description: promotion.description ?? '',
    kind: promotion.kind,
    value: formatDecimalInput(
      promotion.kind === PromotionKind.FIXED ? fromMinorUnits(promotion.value, 'USD') : promotion.value,
    ),
    startsAt: isoToLocalInput(promotion.startsAt),
    endsAt: isoToLocalInput(promotion.endsAt),
    active: promotion.active,
    code: promotion.code ?? '',
  }));

  const set = <K extends keyof PromotionFormValues>(key: K, value: PromotionFormValues[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => clearFieldError(e, key));
    setError(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const result = validatePromotionForm(form, { requireFutureEnd: false });
    setErrors(result.errors);
    if (!result.payload) return;

    try {
      await update.mutateAsync({ id: promotion.promotionId, input: result.payload });
      navigate(basePath);
    } catch (err) {
      const { field, message } = promotionServerError(err, 'No se pudo guardar la promoción.');
      if (field) setErrors({ [field]: message });
      else setError(message);
    }
  };

  return (
    <CardContent className="pt-5">
      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        {error ? (
          <p className="rounded-md border border-danger-soft bg-danger-soft px-3 py-2 text-sm font-medium text-danger" role="alert">
            {error}
          </p>
        ) : null}

        <PromotionFormFields form={form} errors={errors} onChange={set} />

        <Button type="submit" disabled={update.isPending}>
          {update.isPending ? 'Guardando…' : 'Guardar cambios'}
        </Button>
      </form>
    </CardContent>
  );
}
